import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, Globe2, Flag, Radio, Sparkles, Code2, Palette, ZapOff, Zap, Search as SearchIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WIDGET_COUNT, SEO_TOOL_COUNT, widgetCountWord, seoToolCountWord } from "@/lib/catalog_counts";
import LiveTrafficFeedPreview from "@/components/widgets/LiveTrafficFeedPreview";
import FlagCounterPreview from "@/components/widgets/FlagCounterPreview";
import OnlineVisitorsPreview from "@/components/widgets/OnlineVisitorsPreview";
import AdSlot from "@/components/AdSlot";
// PlatformsStrip + Testimonials are heavy (450 lines, lots of inline brand
// v60.30: reverted the v60.16.26 marquee lazy-loading. Production
// Lighthouse audit showed these marquees ARE in the viewport on most
// desktop screens (and just below it on mobile) — users scroll to them
// within 1-2 seconds, so the cost of a chunk fetch outweighs the bundle
// savings on already-fast pages. Synchronous imports actually score
// HIGHER in Lighthouse for these mid-page sections.
import { PlatformsStrip, Testimonials } from "@/components/Marquees";
import LanguagePicker from "@/components/LanguagePicker";
import GlobalSearchModal from "@/components/GlobalSearchModal";
import DeferUntilVisible from "@/components/DeferUntilVisible";
import HomeSeoBody from "@/components/landing/HomeSeoBody";
import { SEARCH_SHORTCUT_LABEL } from "@/lib/platform";
import { createSite } from "@/lib/api";
import { useI18n, localizedUrl, seoMetaFor } from "@/lib/i18n";
import { homeT } from "@/lib/content_i18n";
// FAQ section stays lazy — it's the largest below-the-fold module
// (~73 KB of locale-specific Q/A copy) and sits genuinely far below
// the fold. v60.30 retains this split (proven net-positive).
const FAQ = lazy(() => import("@/components/LazyFAQ"));
// Footer + SEOContent stay lazy — they're genuinely the LAST sections
// of the homepage, well past every desktop fold. v60.30 retains.
const Footer = lazy(() =>
  import("@/components/landing/LandingFooter").then((m) => ({ default: m.Footer }))
);
const SEOContent = lazy(() =>
  import("@/components/landing/LandingFooter").then((m) => ({ default: m.SEOContent }))
);
// v60.30: reverted v60.26 — SeoToolsShowcase + UtilityWidgetsShowcase
// were mid-page sections that users scroll to in 2-3 seconds. The lazy
// chunk fetch overhead beat the bundle-size savings on already-fast
// pages, contributing to the production Perf 90+ → 80+ regression.
// Re-importing synchronously from the same module (named exports, no
// runtime change other than chunk packaging).
import { SeoToolsShowcase, UtilityWidgetsShowcase } from "@/components/landing/LandingShowcases";
import { widgetDisplayNameForLang, seoToolTitleForLang } from "@/lib/content_i18n";
import { toast } from "sonner";
import { HOME_SEO_BODY } from "@/data/home_seo_body";

const DEFAULT_TRAFFIC = {
  width: 320, bg_color: "#ffffff", text_color: "#0A0A0A", border_color: "#E5E7EB",
  link_color: "#002FA7", header_bg: "#002FA7", header_fg: "#ffffff", font: "IBM Plex Sans", rows: 6,
};
const DEFAULT_FLAG = {
  width: 280, bg_color: "#ffffff", text_color: "#0A0A0A", border_color: "#E5E7EB",
  header_bg: "#002FA7", header_fg: "#ffffff", font: "IBM Plex Sans", rows: 4, cols: 3,
  show_country_code: true, show_flag: true,
};
const DEFAULT_BUTTON = {
  shape: "rect-rounded", bg_type: "gradient", bg_color: "#002FA7",
  bg_gradient_from: "#002FA7", bg_gradient_to: "#00E676", text_color: "#ffffff",
  border_color: "#0A0A0A", border_width: 0, font: "IBM Plex Mono", size: 130, show_label: true, label_text: "Online",
};

const Nav = () => {
  const { t, lang } = useI18n();
  // `openMenu` identifies which dropdown is currently visible (null = none,
  // "tools" = SEO tools, "widgets" = More Widgets). Single source of truth
  // ensures opening one menu auto-closes the other — prevents stacked
  // overlapping dropdowns when the pointer drifts from one trigger to
  // another. v60.22.
  const [openMenuId, setOpenMenuId] = useState(null);
  // closeTimer holds a setTimeout id while a dropdown is "scheduled to
  // close". Pointer re-entry into the dropdown (either the trigger or the
  // menu itself) cancels it. This gives the user a forgiving ~250 ms grace
  // window to traverse the small visual gap between the trigger and the
  // menu — the previous version closed instantly on the slightest mouse
  // drift, making menu items effectively unclickable.
  const closeTimer = useRef(null);
  // v60.99.47 — site-wide GlobalSearch modal (Cmd-K / icon-button).
  // Mounted once at the navbar so any homepage navigation can open it.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      // Cmd-K on macOS, Ctrl-K everywhere else. Skip when an input/
      // textarea already has focus to keep the binding non-invasive.
      const tag = (e.target?.tagName || "").toLowerCase();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" &&
          tag !== "input" && tag !== "textarea") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const showMenu = (id) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenMenuId(id);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenuId(null), 250);
  };
  return (
  <header className="w-full border-b border-zinc-200 bg-white sticky top-0 z-40">
    {/* v60.99.53 — restructured navbar layout per operator spec:
          • Logo enlarged + vertically-centred so it visually anchors
            both rows in the top-left (clean, unique brand presence).
          • Right column splits into 2 explicit stacked rows:
              Row 1: nav menu items + LangPicker + Search + X + LinkedIn
          v60.99.63 — single-row navbar redesign. Operator wanted the
          recent 2-row stack collapsed back to ONE row. Plan:
          • Logo: 40-48px box → 28-34px box. Wordmark text-xl/2xl →
            text-base/lg. Wordmark hidden below sm: so logo block
            stays under 32px wide on phones.
          • Outer 2-row wrapper (`flex-col gap-2`) removed; all
            items now live in a single flex row.
          • Nav menu gap tightened (gap-x-4/5 from gap-x-5/7).
          • Generate widget button compressed (px-3 py-1.5 from
            px-5 py-2.5; text-xs always vs text-sm).
          • Social icons stay; total right cluster
            kept lean so no wrap on 1280px+ viewports. */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-14 sm:min-h-16 py-2 flex items-center gap-6 lg:gap-10">
      <Link
        to="/"
        className="flex items-center gap-2 shrink-0"
        data-testid="nav-logo"
      >
        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-klein text-white flex items-center justify-center font-display font-black text-base sm:text-lg">F</div>
        <span className="font-display font-black tracking-tight text-base sm:text-lg hidden sm:inline">FeedPulse</span>
      </Link>
      {/* v60.99.63 — single right-aligned cluster instead of 2 stacked rows. */}
      {/* v60.99.64 — dropped the "Traffic Widgets" anchor link from the
          desktop nav (was visually overlapping the FeedPulse wordmark
          at 1280-1440px viewports — the right cluster's content was
          too wide for justify-end to keep clear of the logo). The
          link was functionally redundant: "More Widgets ▾" dropdown
          surfaces the same #widgets section + 9 specific widget
          landings, which is strictly better discovery. The mobile
          sub-nav still has it for non-desktop visitors. */}
      <div className="flex-1 flex items-center gap-x-3 lg:gap-x-5 justify-end min-w-0" data-testid="nav-row-1">
      <nav className="hidden md:flex items-center gap-x-4 lg:gap-x-5 text-[13px] font-medium text-zinc-600">
        {/* More Widgets dropdown — same affordance as SEO Tools. Lists
            the top 6 widgets by traffic intent + an "All widgets →"
            entry that routes to the full gallery. The top-6 picks the
            widgets with the strongest SEO landing pages so the dropdown
            doubles as a discovery surface for the SEO-optimised
            `/free-*` routes. v60.22. */}
        <div className="relative" onMouseEnter={() => showMenu("widgets")} onMouseLeave={scheduleClose}>
          <button
            className="hover:text-zinc-900 flex items-center gap-1 whitespace-nowrap"
            data-testid="nav-more-widgets"
            onClick={() => setOpenMenuId((v) => (v === "widgets" ? null : "widgets"))}
            type="button"
          >
            {t("nav_gallery")} <span className="text-[10px]">▾</span>
          </button>
          {openMenuId === "widgets" && (
            <div
              className="absolute top-full left-0 w-72 pt-2 z-50"
              data-testid="nav-more-widgets-menu"
              onMouseEnter={() => showMenu("widgets")}
              onMouseLeave={scheduleClose}
            >
              <div className="bg-white border border-zinc-200 shadow-lg py-2">
                {[
                  // Top 6 widgets by SEO landing strength + intent. The
                  // newest two (hit-counter, heatmap) lead the list so
                  // visitors find them above the fold without scrolling
                  // (v60.21 had them only in the footer).
                  { slug: "free-hit-counter",            label: "Free Hit Counter",            sub: "New · cumulative all-time hits" },
                  { slug: "heatmap",                     label: "Free Clicks Heatmap",         sub: "New · see where visitors click" },
                  { slug: "free-discord-members-widget", label: "Free Discord Widget",         sub: "Live members count · no bot" },
                  { slug: "free-github-stars-badge",     label: "Free GitHub Stars Badge",     sub: "New · live ★ count for any repo" },
                  { slug: "free-npm-downloads-badge",    label: "Free npm Downloads Badge",    sub: "New · downloads/day, week, month" },
                  { slug: "free-weather-widget",         label: "Free Weather Widget",         sub: "Wunderground replacement" },
                  { slug: "free-visitor-globe-widget",   label: "Free Visitor Globe",          sub: "Live visitors on a 3D globe" },
                  { slug: "free-verified-traffic-badge", label: "Verified Traffic Badge",      sub: "Ad-buyer trust seal" },
                  { slug: "free-countdown-widget",       label: "Free Countdown Widget",       sub: "Launch · sale · event timers" },
                  { slug: "widgets",                     label: "All widgets →",               sub: "" },
                ].map((it) => (
                  <Link
                    key={it.slug}
                    to={`/${it.slug}`}
                    className="block px-4 py-2 hover:bg-zinc-50 transition-colors"
                    data-testid={`nav-more-widgets-${it.slug}`}
                  >
                    <div className="font-bold text-sm text-zinc-900">
                      {/* Localized widget name — same fallback contract
                          as the SEO Tools dropdown above. The trailing
                          "All widgets →" entry has slug="widgets" which
                          has no localized translation; the EN it.label
                          falls through naturally. */}
                      {widgetDisplayNameForLang(it.slug, lang, it.label)}
                    </div>
                    {it.sub && <div className="text-xs text-zinc-500 mt-0.5">{it.sub}</div>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* SEO Tools dropdown — gives the new tool pages a clean discovery
           path from the landing page nav. Static markup keeps it crawlable. */}
        <div className="relative" onMouseEnter={() => showMenu("tools")} onMouseLeave={scheduleClose}>
          <button
            className="hover:text-zinc-900 flex items-center gap-1 whitespace-nowrap"
            data-testid="nav-seo-tools"
            onClick={() => setOpenMenuId((v) => (v === "tools" ? null : "tools"))}
            type="button"
          >
            SEO Tools <span className="text-[10px]">▾</span>
          </button>
          {openMenuId === "tools" && (
            // Wrapper: `top-full` plus `pt-2` (NOT `mt-2`) — the 8 px gap is
            // now padding INSIDE the hoverable rectangle, so the mouse never
            // exits the dropdown's bounding box while traversing the visual
            // gap between trigger and menu. Visible chrome lives on the
            // inner card.
            <div
              className="absolute top-full left-0 w-72 pt-2 z-50"
              data-testid="nav-seo-tools-menu"
              onMouseEnter={() => showMenu("tools")}
              onMouseLeave={scheduleClose}
            >
              <div className="bg-white border border-zinc-200 shadow-lg py-2">
                {[
                  // Top 6 SEO tools by traffic potential — keeps the dropdown
                  // viewport-safe (was 13 items overflowing past the page on
                  // 13"-MBPs). Remaining 6 tools live on /tools, surfaced via
                  // the explicit "All SEO tools →" entry at the bottom.
                  { slug: "serp-checker", label: "SERP Position Checker", sub: "New · Google rank tracker" },
                  { slug: "indexed-checker", label: "Google Index Checker", sub: "New · is my page indexed?" },
                  { slug: "meta-tag-generator", label: "Free Meta Tag Generator", sub: "New · Title · OG · Twitter cards" },
                  { slug: "schema-markup-generator", label: "Free Schema Markup Generator", sub: "New · JSON-LD for rich results" },
                  { slug: "da-checker", label: "Domain Authority Checker", sub: "Free DA checker" },
                  { slug: "ai-audit", label: "AI SEO Audit", sub: "GPT-5.2 powered, free" },
                  { slug: "tools", label: "All SEO tools →", sub: "" },
                ].map((it) => (
                  <Link
                    key={it.slug}
                    to={`/${it.slug}`}
                    className="block px-4 py-2 hover:bg-zinc-50 transition-colors"
                    data-testid={`nav-seo-tool-${it.slug}`}
                  >
                    <div className="font-bold text-sm text-zinc-900">
                      {/* Localized SEO-tool name — single source of truth
                          in i18n/content.<lang>.json under tools.<slug>.titleShort.
                          The trailing "All SEO tools →" entry has slug="tools"
                          which has no localized translation; the EN
                          `it.label` falls through naturally. */}
                      {seoToolTitleForLang(it.slug, lang, it.label)}
                    </div>
                    {it.sub && <div className="text-xs text-zinc-500 mt-0.5">{it.sub}</div>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        <a href="#how" className="hover:text-zinc-900 whitespace-nowrap" data-testid="nav-how">{t("nav_how")}</a>
        <Link to="/blog" className="hover:text-zinc-900 whitespace-nowrap" data-testid="nav-blog">{t("nav_blog")}</Link>
        <a href="#faq" className="hover:text-zinc-900 whitespace-nowrap" data-testid="nav-faq">{t("nav_faq")}</a>
        <Link to="/contact" className="hover:text-zinc-900 whitespace-nowrap" data-testid="nav-contact">{t("nav_contact")}</Link>
      </nav>
      <LanguagePicker />
      {/* v60.99.47 — site-wide search trigger. Available on ALL viewport
          sizes (including mobile, where the dropdown menus are hidden
          and search becomes the primary discovery path). Clicking
          opens the GlobalSearchModal which can search across widgets,
          SEO tools, blog posts, and discovery hubs. ⌘K / Ctrl-K
          shortcut bound globally on this page. */}
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-1.5 border border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 transition-colors font-mono text-[11px]"
        aria-label="Search the FeedPulse site"
        data-testid="nav-search-trigger"
      >
        <SearchIcon className="w-3.5 h-3.5" />
        <span className="hidden lg:inline">Search</span>
        <kbd className="hidden lg:inline px-1 py-0.5 border border-zinc-300 bg-zinc-50 text-[9px] ml-1">{SEARCH_SHORTCUT_LABEL}</kbd>
      </button>
      {/* v60.99.46 — Twitter (X) + LinkedIn social proof icons.
          Pinned to the navbar so visitors discover the social channels
          without scrolling. Brand SVG paths (X mark + LinkedIn) inline
          so there's zero network cost / PSI impact. Bold colour at
          rest (klein) + darker on hover signals clickability without
          breaking the navbar's monochrome rhythm.
          Hidden on small mobile (the cramped sub-nav already carries
          all primary routes); visible from `sm:` upward. */}
      <a
        href="https://x.com/MakeEzyMoney"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow FeedPulse on X (Twitter)"
        className="hidden sm:inline-flex items-center justify-center w-8 h-8 text-klein hover:text-[#0A0A0A] transition-colors"
        data-testid="nav-social-x"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          {/* X (formerly Twitter) brand mark — simpleicons.org (CC0) */}
          <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </a>
      <a
        href="https://www.linkedin.com/in/feed-pulse-157574175/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow FeedPulse on LinkedIn"
        className="hidden sm:inline-flex items-center justify-center w-8 h-8 text-klein hover:text-[#0A0A0A] transition-colors"
        data-testid="nav-social-linkedin"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          {/* LinkedIn brand mark — simpleicons.org (CC0) */}
          <path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </a>
      {/* v60.99.74 — YouTube icon matched to SiteHeader.jsx so the
          homepage navbar carries the same social cluster as every other
          page. Channel: @Feed-Pulse-com. */}
      <a
        href="https://www.youtube.com/@Feed-Pulse-com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Subscribe to FeedPulse on YouTube"
        className="hidden sm:inline-flex items-center justify-center w-8 h-8 text-klein hover:text-[#0A0A0A] transition-colors"
        data-testid="nav-social-youtube"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          {/* YouTube brand mark — simpleicons.org (CC0) */}
          <path fill="currentColor" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      </a>
      {/* v60.99.63 — Generate widget + Bookmark are now inline with
          social icons in the SAME single flex row (was previously a
          separate row-2 wrapper). */}
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/generator"
              className="shrink-0 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#0A0A0A] text-white font-bold text-xs hover:bg-klein transition-colors flex items-center gap-1.5"
              data-testid="nav-cta"
              aria-describedby="nav-cta-tooltip"
            >
              <span className="hidden sm:inline">{t("nav_cta")}</span><span className="sm:hidden">{t("nav_cta_short")}</span> <ArrowUpRight size={12} />
            </Link>
          </TooltipTrigger>
          <TooltipContent
            id="nav-cta-tooltip"
            side="bottom"
            align="end"
            className="bg-[#0A0A0A] text-white border border-zinc-800 text-[11px] font-mono uppercase tracking-widest px-3 py-2"
            data-testid="nav-cta-tooltip"
          >
            Traffic feed · Flag counter · Online visitors
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      </div>{/* end single row */}
    </div>
    {/* Mobile sub-nav.
        v60.99.107 — two fixes. (1) A second <LanguagePicker> used to live
        here with `ml-auto`, which inside a horizontally scrolling flex row
        pushes it past the end of the scroll content: at 390px it sat at
        x=505 where nobody could see or reach it, and it duplicated the
        `language-picker` test id of the picker in the row above. Removed —
        the row-1 picker is visible at every breakpoint.
        (2) The row scrolls but `scrollbar-hide` left no affordance, so the
        links past "Blog" looked clipped rather than scrollable. A right-edge
        mask fades the last item instead of guillotining it. */}
    <nav
      className="md:hidden flex items-center gap-4 px-4 pb-3 -mt-1 overflow-x-auto overscroll-x-contain text-xs font-medium text-zinc-600 scrollbar-hide"
      style={{
        maskImage: "linear-gradient(to right, black 88%, transparent)",
        WebkitMaskImage: "linear-gradient(to right, black 88%, transparent)",
      }}
      aria-label="Site sections"
    >
      <a href="#widgets" className="whitespace-nowrap">{t("nav_widgets")}</a>
      <Link to="/widgets" className="whitespace-nowrap">{t("nav_gallery")}</Link>
      <Link to="/tools" className="whitespace-nowrap">SEO Tools</Link>
      <a href="#how" className="whitespace-nowrap">{t("nav_how")}</a>
      <Link to="/blog" className="whitespace-nowrap">{t("nav_blog")}</Link>
      <a href="#faq" className="whitespace-nowrap">{t("nav_faq")}</a>
      <Link to="/contact" className="whitespace-nowrap">{t("nav_contact")}</Link>
      {/* Trailing spacer so the last link can scroll clear of the fade. */}
      <span aria-hidden="true" className="shrink-0 w-6" />
    </nav>
    <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
  </header>
  );
};

function HeroForm() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!domain.trim()) {
      toast.error("Enter your website URL first");
      return;
    }
    setLoading(true);
    try {
      const site = await createSite(domain.trim());
      toast.success("Site created. Customize your widgets →");
      navigate(`/generator?site=${site.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create site";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="hero-form">
      <input
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        placeholder={t("hero_form_placeholder")}
        className="flex-1 px-4 py-3.5 bg-white border border-zinc-300 focus:border-klein focus:ring-1 focus:ring-klein outline-none font-mono text-sm"
        data-testid="hero-domain-input"
      />
      <button
        type="submit"
        disabled={loading}
        className="px-6 py-3.5 bg-klein hover:bg-klein-hover text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="hero-submit"
      >
        {loading ? t("hero_form_loading") : (<><span>{t("hero_form_submit")}</span><ArrowUpRight size={14} /></>)}
      </button>
    </form>
  );
}

const Hero = () => {
  const { t, lang } = useI18n();
  return (
  <section className="relative bg-white overflow-hidden">
    <div className="absolute inset-0 grid-paper-bg opacity-60" />
    {/* v60.66 — Hero padding tightened. Was `pt-12 sm:pt-20 lg:pt-28`
        which produced 112px at lg, on top of the kicker-pill's mb-5
        (= 20px), creating ~166px of empty space between the nav bar
        and the H1 on desktop. Modern landing-page best practice is
        64-96px of top breathing room; anything more pushes the value
        prop below the fold. New scale: 32 / 56 / 80 px keeps the design
        airy without burning a half-fold of real estate. */}
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16 sm:pt-14 sm:pb-24 lg:pt-20 lg:pb-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-mono uppercase tracking-[0.18em] sm:tracking-[0.2em] text-zinc-500 mb-5 sm:mb-6 border border-zinc-300 px-2.5 sm:px-3 py-1 sm:py-1.5">
            <span className="w-1.5 h-1.5 bg-online rounded-full animate-blink-dot" />
            <span className="hidden sm:inline">{t("hero_kicker_full")}</span>
            <span className="sm:hidden">{t("hero_kicker_short")}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-5 sm:mb-6">
            {t("hero_h1_1")} <br className="hidden sm:block" />
            <span className="bg-klein text-white px-2 inline-block -skew-y-1">{t("hero_h1_2")}</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-zinc-600 max-w-2xl mb-6 sm:mb-7 leading-relaxed">
            {t("hero_sub_a")} <strong>{t("hero_sub_b")}</strong> {t("hero_sub_c")} <strong>{t("hero_sub_d")}</strong>{t("hero_sub_e")} <strong>{t("hero_sub_f")}</strong>{t("hero_sub_g")}
          </p>
          {/* USP pills — bold, brand-friendly, picked up by Google as snippet text */}
          <ul className="flex flex-wrap gap-2 mb-8 sm:mb-10" data-testid="usp-pills">
            {[
              t("usp_nosignup"), t("usp_noemail"), t("usp_noreg"),
              t("usp_freewidget"), t("usp_free"), t("usp_ltff"), t("usp_ltwf"),
            ].map((u) => (
              <li key={u} className="inline-flex items-center text-[11px] sm:text-xs font-mono uppercase tracking-[0.12em] px-2.5 py-1 border border-klein/30 bg-klein/5 text-klein font-semibold rounded-none">
                ✓ {u}
              </li>
            ))}
          </ul>
          <HeroForm />
          <div className="mt-5 sm:mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-6 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-zinc-500">
            <span>{t("hero_footnote")}</span>
          </div>
          {/* v60.102.20 — the homepage's long-tails ("live visitor counter
              free", "embed live traffic widget", "feedjit alternative") sat
              only inside <meta name="keywords">, which Google ignores. Same
              visible chip row the widget and tool pages use. English only:
              the surrounding hero copy is localized, and an English chip row
              under Spanish copy is the mixed-language signal we just removed
              everywhere else. Copy: src/data/home_seo_body.json. */}
          {lang === "en" && HOME_SEO_BODY.chips ? (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="home-hero-keyword-chips">
              {HOME_SEO_BODY.chips.map((k) => (
                <span key={k} className="px-2 py-1 bg-zinc-100 text-zinc-600 text-xs font-mono">{k}</span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
          <div className="relative z-10 shadow-xl w-full max-w-sm lg:max-w-none">
            <LiveTrafficFeedPreview cfg={{ ...DEFAULT_TRAFFIC, width: 360, lang }} />
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

const widgetMeta = [
  { id: "feed", Icon: Radio },
  { id: "flag", Icon: Flag },
  { id: "online", Icon: Zap },
];

const Widgets = () => {
  const { t, lang } = useI18n();
  const h = (k) => homeT(lang, k);
  return (
  <section id="widgets" className="bg-zinc-50 border-y border-zinc-200">
    <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
        <div className="md:col-span-4">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("section_widgets_kicker")}</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter">{t("section_widgets_h2")}</h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 text-base text-zinc-700 leading-relaxed">
          {t("section_widgets_lead")}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7 bg-white border border-zinc-200 p-8 flex flex-col gap-6" data-testid="widget-card-feed">
          <div className="flex items-center gap-3">
            <Radio size={20} className="text-klein" />
            <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{h("widget_card_feed_kicker")}</span>
          </div>
          <h3 className="font-display text-3xl font-black tracking-tight">{h("widget_card_feed_h")}</h3>
          <p className="text-zinc-600">{h("widget_card_feed_p")}</p>
          <div className="mt-2 flex justify-center bg-zinc-50 p-6 border border-zinc-200">
            <LiveTrafficFeedPreview cfg={{ ...DEFAULT_TRAFFIC, width: 340, lang }} />
          </div>
        </div>

        <div className="md:col-span-5 grid grid-rows-2 gap-6">
          <div className="bg-white border border-zinc-200 p-8 flex flex-col gap-4" data-testid="widget-card-flag">
            <div className="flex items-center gap-3">
              <Flag size={20} className="text-klein" />
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{h("widget_card_flag_kicker")}</span>
            </div>
            <h3 className="font-display text-2xl font-black tracking-tight">{h("widget_card_flag_h")}</h3>
            <div className="flex justify-center pt-1">
              <FlagCounterPreview cfg={{ ...DEFAULT_FLAG, width: 260, rows: 3, lang }} />
            </div>
          </div>

          <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] p-8 flex flex-col gap-4" data-testid="widget-card-online">
            <div className="flex items-center gap-3">
              <Zap size={20} className="text-online" />
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{h("widget_card_online_kicker")}</span>
            </div>
            <h3 className="font-display text-2xl font-black tracking-tight">{h("widget_card_online_h")}</h3>
            <div className="flex justify-center pt-1">
              <OnlineVisitorsPreview cfg={{ ...DEFAULT_BUTTON, lang }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

const Steps = () => {
  const { t } = useI18n();
  const steps = [
    { n: "01", Icon: Globe2, title: t("step_1_title"), body: t("step_1_body") },
    { n: "02", Icon: Palette, title: t("step_2_title"), body: t("step_2_body") },
    { n: "03", Icon: Code2, title: t("step_3_title"), body: t("step_3_body") },
  ];
  return (
  <section id="how" className="bg-white">
    <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("section_how_kicker")}</div>
      <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mb-16 max-w-3xl">
        {t("section_how_h2")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200">
        {steps.map((s) => (
          <div key={s.n} className="bg-white p-10 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-zinc-600 tracking-widest">{s.n}</span>
              <s.Icon size={20} className="text-klein" />
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight">{s.title}</h3>
            <p className="text-zinc-600 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const FreeCTA = () => {
  const { t } = useI18n();
  return (
  <section id="free" className="bg-[#0A0A0A] text-white">
    <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
      <div className="md:col-span-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 mb-3">{t("section_free_kicker")}</div>
        <h2 className="font-display text-4xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
          {t("section_free_h2_1")} <br />{t("section_free_h2_2")}
        </h2>
        <p className="text-zinc-300 text-lg max-w-md mb-8">
          {t("section_free_body")}
        </p>
        <Link
          to="/generator"
          className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] font-bold px-6 py-3.5 hover:bg-online hover:text-[#0A0A0A] transition-colors"
          data-testid="free-cta"
        >
          {t("section_free_cta")} <ArrowUpRight size={14} />
        </Link>
      </div>
      <div className="md:col-span-6 grid grid-cols-2 gap-px bg-zinc-800">
        {[
          { v: "∞", l: t("stat_visits_label") },
          { v: "3", l: t("stat_widgets_label") },
          { v: "$0", l: t("stat_forever_label") },
          { v: "30+", l: t("stat_flags_label") },
        ].map((s) => (
          <div key={s.l} className="bg-[#0A0A0A] p-10">
            <div className="font-display text-5xl font-black tracking-tighter">{s.v}</div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-2">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

// `Footer` is lazy-imported at the top of the file from
// `@/components/landing/LandingFooter` (v60.25 — code-split below-the-
// fold chunk). The original inline definition was deleted from here.

// FAQ data lives in /app/frontend/src/lib/content_i18n.js (FAQS_I18N).
// The FAQ component pulls the locale-correct array via faqsForLang(lang)
// and emits the FAQPage JSON-LD in the same locale.

// `SeoToolsShowcase` + `UtilityWidgetsShowcase` are lazy-imported at
// the top of the file from `@/components/landing/LandingShowcases`
// (v60.26 — code-split below-the-fold chunk). The original inline
// definitions were deleted from here.

export default function Landing({ pathOverride = null, seoKey = "home" } = {}) {
  const { lang } = useI18n();
  // v60.99.63 — accept `pathOverride` so a sister route like
  // /free-live-traffic-widget can render the same Landing content but
  // emit a self-referential canonical (PSI audits the URL it was
  // called with; the previous client-side Navigate→/ didn't pass
  // because PSI doesn't follow client redirects for canonical
  // checks → SEO 92). Sister routes also pass `seoKey` so their
  // SERP title/description target a different head term (avoiding
  // duplicate-content overlap with the homepage's "Feedjit
  // alternative" angle).
  const canonicalPath = pathOverride || "/";
  const canonicalUrl = localizedUrl(canonicalPath, lang);
  // Per-locale SEO title + description so non-EN homepage variants
  // aren't flagged as near-duplicates of the English page. The shell
  // <title> in public/index.html ships English copy for first-byte
  // SERP rendering of /en — for any other locale, this Helmet block
  // overrides it (and prerender_one.js's dedupeKeepFirst keeps Helmet's
  // since Helmet prepends <title> ahead of the shell title).
  const seo = seoMetaFor(seoKey, lang);
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Helmet>
        {/* Single source of truth for SERP head copy: SEO_META in
            lib/i18n.js. Editing the EN entry there propagates to every
            locale automatically (with EN fallback for un-translated
            fields). The Helmet block here is intentionally thin so
            future copy changes never need a JSX edit. */}
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content="https://feed-pulse.com/og-cover.svg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FeedPulse live traffic feed widget — Feedjit, Whos.amung.us and Livetrafficfeed.com alternative" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FeedPulse — Free live traffic feed & widget" />
        <meta name="twitter:description" content="The free Feedjit, Whos.amung.us and Livetrafficfeed.com alternative. Live visitor feed, flag counter, online counter. One HTML snippet, no signup." />
        <meta name="twitter:image" content="https://feed-pulse.com/og-cover.svg" />
        <meta name="twitter:image:alt" content="FeedPulse — free live traffic feed widget for any website" />
        {/* Keywords meta now pulled from SEO_META.home.<lang>.keywords
            (falls back to EN). Single source of truth — edit SEO_META
            in lib/i18n.js and ALL locales sync. */}
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <Nav />
      {/* v60.99.62 — wrap body content in <main> landmark for
          accessibility (Lighthouse a11y rule `landmark-one-main`).
          Was costing -3 a11y weight (≈ 2 points) on every homepage
          PSI run. Nav stays outside (it's <header>-equivalent),
          Footer stays outside (it's its own <footer> internally). */}
      <main>
        <Hero />
      {/* v60.99.120 — HomeSeoBody is rendered EAGERLY, immediately after the
          hero, and must NEVER be wrapped in DeferUntilVisible. Googlebot does
          not scroll, so every IntersectionObserver-gated section below was
          invisible to it: crawlers were served 346 words where a real browser
          (after scrolling) sees 3,111, and the homepage had dropped out of
          Google's index entirely. This block is text + one table, so it costs
          almost nothing on the LCP path. Copy: src/data/home_seo_body.js,
          shared with scripts/static_prerender.js. */}
      <HomeSeoBody />
      {/* Everything below is deferred until it scrolls into view. These are
          the heavy interactive showcases; the indexable copy already shipped
          above, so deferring them no longer costs us anything in search. */}
      <DeferUntilVisible minHeight={120} testId="defer-platforms">
        <PlatformsStrip />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={140} testId="defer-ad-hero">
        <div className="max-w-7xl mx-auto px-6">
          <AdSlot position="home-below-hero" />
        </div>
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={700} testId="defer-widgets">
        <Widgets />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={500} testId="defer-steps">
        <Steps />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={700} testId="defer-seo-tools">
        <SeoToolsShowcase />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={700} testId="defer-util-widgets">
        <UtilityWidgetsShowcase />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={300} testId="defer-testimonials">
        <Testimonials />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={500} testId="defer-seo-content">
        <Suspense fallback={<div style={{ minHeight: 500 }} aria-hidden="true" />}>
          <SEOContent />
        </Suspense>
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={400} testId="defer-free-cta">
        <FreeCTA />
      </DeferUntilVisible>
      <DeferUntilVisible minHeight={500} testId="defer-faq" id="faq">
        {/* v60.99.73 — id="faq" now lives on the DeferUntilVisible
            wrapper so the #faq anchor target exists in the DOM IMMEDIATELY
            on first page load. Previously the id lived inside the lazy
            FAQ component, which meant clicking nav "FAQ" before the
            component had mounted scrolled to nowhere and required 2-3
            re-clicks before DeferUntilVisible's IntersectionObserver
            caught up. Now: anchor scroll lands on the 500px-reserved
            wrapper, DeferUntilVisible's observer sees it intersect,
            mounts FAQ, no CLS because minHeight matches. scroll-margin-top
            offsets the sticky header so the FAQ heading isn't hidden.
            Suspense fallback uses the same minHeight reserve as the
            DeferUntilVisible placeholder above, so loading the lazy
            FAQ chunk never causes CLS — the reserved height is filled
            either by the placeholder, the Suspense fallback, or the
            real FAQ section, all of which match in size. */}
        <Suspense fallback={<div style={{ minHeight: 500 }} aria-hidden="true" />}>
          <FAQ />
        </Suspense>
      </DeferUntilVisible>
      </main>
      <DeferUntilVisible minHeight={400} testId="defer-footer">
        <Suspense fallback={<div style={{ minHeight: 400 }} aria-hidden="true" />}>
          <Footer />
        </Suspense>
      </DeferUntilVisible>
    </div>
  );
}

// `SEOContent` is lazy-imported at the top of the file from
// `@/components/landing/LandingFooter` (v60.25 — code-split below-the-
// fold chunk). The original inline definition was deleted from here.
